import { useLanguage } from '../context/LanguageContext';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';

export default function PolitiqueCookies() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#00aaff] mb-6 transition-colors">
          <ArrowLeft size={18} />
          <span className="text-sm">Retour à l'accueil</span>
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <Cookie size={48} className="text-[#00aaff]" />
          <h1 className="text-3xl md:text-5xl font-black text-gray-900">Politique de Cookies</h1>
        </div>

        <div className="prose prose-gray max-w-none space-y-8">
          {/* Intro */}
          <section>
            <p className="text-gray-700 leading-relaxed mb-4">
              Le site <strong>joeycyan.com</strong> utilise des cookies et autres technologies similaires pour améliorer votre expérience de navigation, analyser le trafic et personnaliser le contenu.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Cette politique vous explique ce que sont les cookies, comment nous les utilisons et comment vous pouvez les gérer.
            </p>
          </section>

          {/* Qu'est-ce qu'un cookie */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Qu'est-ce qu'un cookie ?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, smartphone, tablette) lorsque vous visitez un site internet. Les cookies permettent au site de :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Reconnaître votre appareil lors de vos prochaines visites</li>
              <li>Mémoriser vos préférences (langue, panier, etc.)</li>
              <li>Analyser la façon dont vous utilisez le site</li>
              <li>Améliorer la performance et la sécurité du site</li>
            </ul>
          </section>

          {/* Types de cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Quels types de cookies utilisons-nous ?</h2>
            
            <div className="space-y-6">
              {/* Cookies strictement nécessaires */}
              <div className="border-l-4 border-green-500 bg-green-50 p-5 rounded-r-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  Cookies strictement nécessaires
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Ces cookies sont indispensables au fonctionnement du site et ne peuvent pas être désactivés.
                </p>
                <div className="bg-white rounded-lg p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 font-semibold text-gray-900">Cookie</th>
                        <th className="text-left py-2 font-semibold text-gray-900">Finalité</th>
                        <th className="text-left py-2 font-semibold text-gray-900">Durée</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      <tr className="border-b border-gray-100">
                        <td className="py-3"><code className="bg-gray-100 px-2 py-1 rounded text-xs">access_token</code></td>
                        <td className="py-3">Gestion de l'authentification</td>
                        <td className="py-3">24h</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3"><code className="bg-gray-100 px-2 py-1 rounded text-xs">refresh_token</code></td>
                        <td className="py-3">Renouvellement de session</td>
                        <td className="py-3">7 jours</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3"><code className="bg-gray-100 px-2 py-1 rounded text-xs">cart_session</code></td>
                        <td className="py-3">Mémorisation du panier</td>
                        <td className="py-3">Session</td>
                      </tr>
                      <tr>
                        <td className="py-3"><code className="bg-gray-100 px-2 py-1 rounded text-xs">cookie_consent</code></td>
                        <td className="py-3">Mémorisation de vos choix cookies</td>
                        <td className="py-3">12 mois</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Cookies de performance */}
              <div className="border-l-4 border-blue-500 bg-blue-50 p-5 rounded-r-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  Cookies de performance et d'analyse
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Ces cookies nous permettent de comprendre comment les visiteurs utilisent le site en collectant des informations de manière anonyme.
                </p>
                <div className="bg-white rounded-lg p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 font-semibold text-gray-900">Cookie</th>
                        <th className="text-left py-2 font-semibold text-gray-900">Finalité</th>
                        <th className="text-left py-2 font-semibold text-gray-900">Durée</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      <tr className="border-b border-gray-100">
                        <td className="py-3"><code className="bg-gray-100 px-2 py-1 rounded text-xs">_ga</code></td>
                        <td className="py-3">Google Analytics - Distinction des utilisateurs</td>
                        <td className="py-3">2 ans</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3"><code className="bg-gray-100 px-2 py-1 rounded text-xs">_gid</code></td>
                        <td className="py-3">Google Analytics - Distinction des utilisateurs</td>
                        <td className="py-3">24h</td>
                      </tr>
                      <tr>
                        <td className="py-3"><code className="bg-gray-100 px-2 py-1 rounded text-xs">_gat</code></td>
                        <td className="py-3">Google Analytics - Limitation du taux de requêtes</td>
                        <td className="py-3">1 minute</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 mt-3 italic">
                  ℹ️ Vous pouvez refuser ces cookies via notre bandeau de consentement.
                </p>
              </div>

              {/* Cookies marketing */}
              <div className="border-l-4 border-purple-500 bg-purple-50 p-5 rounded-r-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                  Cookies marketing et publicitaires
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Ces cookies permettent de vous proposer des publicités personnalisées en fonction de vos centres d'intérêt.
                </p>
                <div className="bg-white rounded-lg p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 font-semibold text-gray-900">Cookie</th>
                        <th className="text-left py-2 font-semibold text-gray-900">Finalité</th>
                        <th className="text-left py-2 font-semibold text-gray-900">Durée</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      <tr className="border-b border-gray-100">
                        <td className="py-3"><code className="bg-gray-100 px-2 py-1 rounded text-xs">_fbp</code></td>
                        <td className="py-3">Facebook Pixel - Publicité ciblée</td>
                        <td className="py-3">3 mois</td>
                      </tr>
                      <tr>
                        <td className="py-3"><code className="bg-gray-100 px-2 py-1 rounded text-xs">ads/ga-audiences</code></td>
                        <td className="py-3">Google Ads - Remarketing</td>
                        <td className="py-3">Session</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 mt-3 italic">
                  ℹ️ Vous pouvez refuser ces cookies via notre bandeau de consentement.
                </p>
              </div>
            </div>
          </section>

          {/* Cookies tiers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Cookies tiers</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Certains cookies sont déposés par des services tiers que nous utilisons :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Google Analytics :</strong> Pour analyser le trafic et l'utilisation du site</li>
              <li><strong>Stripe :</strong> Pour le traitement sécurisé des paiements</li>
              <li><strong>Facebook Pixel :</strong> Pour les campagnes publicitaires (si accepté)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Ces services tiers ont leur propre politique de confidentialité et d'utilisation des cookies.
            </p>
          </section>

          {/* Consentement */}
          <section className="bg-[#00aaff]/5 border-l-4 border-[#00aaff] p-6 rounded-r-xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Votre consentement</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Conformément à la réglementation européenne et française (RGPD, directive ePrivacy), nous vous demandons votre consentement avant de déposer des cookies non essentiels sur votre appareil.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Lors de votre première visite, un bandeau d'information apparaît en bas de votre écran pour vous permettre de :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Accepter tous les cookies</li>
              <li>Refuser les cookies non essentiels</li>
              <li>Personnaliser vos préférences par catégorie</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Votre choix est conservé pendant 12 mois. Vous pouvez modifier vos préférences à tout moment en cliquant sur le lien "Gérer les cookies" en bas de page.
            </p>
          </section>

          {/* Gérer les cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Comment gérer et supprimer les cookies ?</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Via notre bandeau de consentement</h3>
                <p className="text-gray-700 leading-relaxed">
                  Vous pouvez gérer vos préférences de cookies à tout moment en cliquant sur "Gérer les cookies" dans le footer du site.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Via votre navigateur</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Vous pouvez configurer votre navigateur pour refuser tous les cookies ou être alerté lorsqu'un cookie est déposé :
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold shrink-0">Chrome :</span>
                    <span className="text-sm">Paramètres → Confidentialité et sécurité → Cookies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold shrink-0">Firefox :</span>
                    <span className="text-sm">Options → Vie privée et sécurité → Cookies et données</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold shrink-0">Safari :</span>
                    <span className="text-sm">Préférences → Confidentialité → Cookies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold shrink-0">Edge :</span>
                    <span className="text-sm">Paramètres → Cookies et autorisations de site</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                <p className="text-amber-900 text-sm leading-relaxed">
                  ⚠️ <strong>Attention :</strong> Le blocage de tous les cookies peut affecter le fonctionnement du site et limiter certaines fonctionnalités (connexion, panier, etc.).
                </p>
              </div>
            </div>
          </section>

          {/* Durée de conservation */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Durée de conservation</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Conformément aux recommandations de la CNIL, la durée de validité des cookies est limitée à :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>13 mois maximum</strong> pour les cookies de mesure d'audience et marketing</li>
              <li><strong>12 mois</strong> pour la conservation de votre consentement</li>
              <li><strong>Session ou durée définie</strong> pour les cookies techniques nécessaires</li>
            </ul>
          </section>

          {/* Plus d'infos */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Pour en savoir plus</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Pour plus d'informations sur les cookies et votre vie privée :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Commission Nationale de l'Informatique et des Libertés (CNIL) : <a href="https://www.cnil.fr/fr/cookies-et-autres-traceurs" target="_blank" rel="noopener noreferrer" className="text-[#00aaff] hover:underline">www.cnil.fr/cookies</a></li>
              <li>Your Online Choices : <a href="https://www.youronlinechoices.com/fr/" target="_blank" rel="noopener noreferrer" className="text-[#00aaff] hover:underline">www.youronlinechoices.com</a></li>
              <li>Politique de Confidentialité de Joey Cyan : <Link to="/politique-confidentialite" className="text-[#00aaff] hover:underline">Voir la politique</Link></li>
            </ul>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 p-6 rounded-xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Pour toute question concernant notre utilisation des cookies :
            </p>
            <ul className="list-none space-y-2 text-gray-700 ml-0">
              <li><strong>Email :</strong> contact@joeycyan.com</li>
              <li><strong>Adresse :</strong> VOYEZ VOUS, 100 RUE DU FAUBOURG DU TEMPLE, 75011 PARIS</li>
            </ul>
          </section>
        </div>

        <p className="text-sm text-gray-400 mt-12">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );
}
